import { useRef, useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useContentToken,
  RichTextContent,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';
import { parseBackgroundVideo } from '../utils/heroVideoUtils';

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Media',
  fields: {
    video_url: {
      label: 'Background Video URL',
      type: 'url',
      defaultField: 'custom_property_featured_video',
      description: 'Optional. Vimeo, YouTube, or direct MP4/WebM. Falls back to image when empty.',
    },
    image: {
      label: 'Featured Image',
      type: 'image',
      // Full-bleed hero photo — matches the Aster-style composition where the
      // property featured image fills the entire hero band behind the title.
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero background',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Find Your Space',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  const welcome = useContentToken('hero.welcome');
  const propertyName = useContentToken('hero.name');
  useComfyCtaStyles();

  // Parallax refs (image-only backgrounds)
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const videoInfo = useMemo(
    () => parseBackgroundVideo(bgImage.video_url),
    [bgImage.video_url],
  );
  const showVideoBackground = !!videoInfo && !prefersReducedMotion;

  useEffect(() => {
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    setVideoReady(false);
    setIframeLoaded(false);
  }, [videoInfo?.src, videoInfo?.type]);

  useEffect(() => {
    if (!showVideoBackground || videoInfo?.type !== 'direct') return;
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => setVideoReady(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setVideoReady(true);

    return () => video.removeEventListener('canplay', onCanPlay);
  }, [showVideoBackground, videoInfo]);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="montserrat"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'montserrat');
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (showVideoBackground) return;
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, [showVideoBackground]);

  return (
    <section ref={sectionRef} className="hero-section relative w-full">
      {/* ---- Full-bleed photo band: featured image + title + CTA, V-cut bottom ---- */}
      <div className="hero-band relative w-full overflow-hidden">
        <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
          {showVideoBackground && videoInfo ? (
            <div className="hero-bg-media absolute inset-0 bg-[var(--vibe-color-text)]">
              {bgImage.image && (
                <VibesitesImage
                  url={bgImage.image}
                  alt={bgImage.alt || ''}
                  priority
                  sizes="100vw"
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                    videoInfo.type === 'embed'
                      ? iframeLoaded
                        ? 'opacity-0'
                        : 'opacity-100'
                      : videoReady
                        ? 'opacity-0'
                        : 'opacity-100'
                  }`}
                />
              )}
              {videoInfo.type === 'embed' ? (
                <iframe
                  src={videoInfo.src}
                  className={`hero-video-iframe absolute inset-0 h-full w-full transition-opacity duration-1000 ${
                    iframeLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  allow="autoplay; fullscreen"
                  frameBorder="0"
                  onLoad={() => setIframeLoaded(true)}
                  title="Background video"
                />
              ) : (
                <video
                  ref={videoRef}
                  src={videoInfo.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                    videoReady ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              )}
            </div>
          ) : (
            /* Parallax + Ken Burns animated background */
            <div
              ref={imgWrapRef}
              className="hero-bg-media absolute h-[140%]"
              style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
            >
              {bgImage.image ? (
                <VibesitesImage
                  url={bgImage.image}
                  alt={bgImage.alt || ''}
                  priority
                  sizes="100vw"
                  className="h-full w-full object-cover"
                  style={{
                    animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                    willChange: 'transform',
                    transformOrigin: 'center center',
                    imageRendering: 'high-quality' as any,
                  }}
                />
              ) : (
                <div className="h-full w-full bg-[var(--vibe-color-text)]" />
              )}
            </div>
          )}
        </EditableSlotGroup>

        {/* Soft dark overlay — keeps the featured photo readable while
            letting architecture and lighting show through (Aster-style). */}
        <div className="hero-overlay absolute inset-0" aria-hidden="true" />

        {/* Headline + CTA centered over the full-bleed photo. Top padding
            clears the fixed utility strip + transparent nav. */}
        <div className="hero-band-inner relative z-10">
          <h1 className="hero-entrance hero-entrance-1 hero-title">
            <EditableToken path="hero.welcome" as="span" className="hero-title-welcome">
              {welcome || 'Welcome to'}
            </EditableToken>
            <EditableToken path="hero.name" as="span" className="hero-title-name">
              {propertyName || 'Property Name'}
            </EditableToken>
          </h1>

          <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || '/floor-plans'}
              target={ctaLink._target || '_self'}
              className="hero-entrance hero-entrance-3 comfy-cta hero-cta"
            >
              {ctaLink.label || 'Find Your Space'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      {/* ---- Lower white band: supporting subtitle under the V seam ---- */}
      <div className="hero-lower relative w-full">
        <div className="hero-lower-inner">
          <div className="hero-copy">
            <RichTextContent
              path="hero.subtitle"
              label="Subtitle"
              defaultValue="<p>Discover thoughtfully designed apartment homes built for the way you want to live.</p>"
              as="div"
              className="hero-entrance hero-entrance-2 hero-subtitle"
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.55s; }

        /* Aster-inspired full-bleed hero: featured photo fills the band,
           content is centered, and a shallow V cuts the bottom edge into
           the white section below. No paint border, no tip accent line. */
        .hero-section {
          isolation: isolate;
          --hero-max: 1440px;
          --hero-v-depth: clamp(2.75rem, 5.5vw, 5rem);
          --hero-band-h: clamp(520px, 78vh, 820px);
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 768px) {
          .hero-section {
            --hero-band-h: clamp(600px, 85vh, 920px);
            --hero-v-depth: clamp(3.25rem, 5vw, 5.5rem);
          }
        }

        .hero-band {
          height: var(--hero-band-h);
          /* Wide shallow V — image fills the band and is clipped to the shape.
             Tip sits on the bottom edge with no decorative line extending below. */
          clip-path: polygon(
            0 0,
            100% 0,
            100% calc(100% - var(--hero-v-depth)),
            50% 100%,
            0 calc(100% - var(--hero-v-depth))
          );
        }

        .hero-overlay {
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.55) 0%,
            rgba(0, 0, 0, 0.42) 45%,
            rgba(0, 0, 0, 0.5) 100%
          );
        }

        /* Cover-fit Vimeo / YouTube iframe backgrounds. */
        .hero-video-iframe {
          border: 0;
          pointer-events: none;
          width: 100vw;
          height: 56.25vw;
          min-height: 100%;
          min-width: 177.78vh;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .hero-band-inner {
          max-width: var(--hero-max);
          margin: 0 auto;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 1.75rem;
          /* Extra top padding clears the fixed top strip + transparent nav
             so the title never sits under the header. Bottom padding keeps
             content above the V tip. */
          padding: calc(var(--offer-banner-height, 0px) + 10.5rem) 1.5rem
            calc(var(--hero-v-depth) + 2.5rem);
          box-sizing: border-box;
        }
        @media (min-width: 768px) {
          .hero-band-inner {
            gap: 2rem;
            padding-left: 4.4rem;
            padding-right: 4.4rem;
            padding-top: calc(var(--offer-banner-height, 0px) + 12.5rem);
            padding-bottom: calc(var(--hero-v-depth) + 3rem);
          }
        }
        @media (min-width: 1024px) {
          .hero-band-inner {
            padding-top: calc(var(--offer-banner-height, 0px) + 13.5rem);
          }
        }

        /* Stacked homepage hero title (original Aion type treatment):
             line 1     → "Welcome to" (Montserrat 400)
             last lines → property name (Montserrat 700), wrapping across
                          ~2 lines for typical names.
           Kept centered to match the full-bleed V-hero composition. */
        .hero-band .hero-title {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
          margin: 0;
          max-width: 100%;
          min-width: 0;
          text-align: center;
          color: #ffffff;
          text-shadow: 0 2px 20px rgba(0, 0, 0, 0.35);
        }
        .hero-band .hero-title-welcome,
        .hero-band .hero-title-name {
          display: block;
          font-family: "Montserrat", var(--vibe-font-heading, sans-serif);
          font-style: normal;
          font-size: clamp(2.5rem, 9vw, 40px);
          line-height: normal;
          letter-spacing: -0.01em;
          color: inherit;
          text-shadow: inherit;
          min-width: 0;
        }
        .hero-band .hero-title-welcome {
          font-weight: 400;
          /* Keep the prefix on a single line above the name stack. */
          white-space: nowrap;
        }
        .hero-band .hero-title-name {
          font-weight: 700;
          /* Wide enough that longer names settle on ~2 lines instead of 3. */
          max-width: 14ch;
          white-space: normal;
          overflow-wrap: break-word;
          word-wrap: break-word;
          text-wrap: balance;
        }
        @media (min-width: 768px) {
          .hero-band .hero-title-welcome,
          .hero-band .hero-title-name {
            font-size: 42px;
          }
          .hero-band .hero-title-name {
            max-width: 16ch;
          }
        }
        @media (min-width: 1024px) {
          .hero-band .hero-title-welcome,
          .hero-band .hero-title-name {
            font-size: 60px;
          }
          .hero-band .hero-title-name {
            max-width: 18ch;
          }
        }

        .hero-cta {
          width: auto;
          min-width: 12rem;
          max-width: 100%;
        }

        .hero-lower {
          background: var(--vibe-color-background, #fff);
        }
        .hero-lower-inner {
          max-width: var(--hero-max);
          margin: 0 auto;
          padding: 2.5rem 1.5rem 3rem;
        }
        @media (min-width: 768px) {
          .hero-lower-inner {
            padding: 3.25rem 4.4rem 4.5rem;
          }
        }
        .hero-copy {
          max-width: 56rem;
          margin: 0 auto;
          text-align: center;
        }
        /* Match PropertyOverview › blurb: Montserrat Light body copy. */
        .hero-subtitle {
          font-family: 'Montserrat', var(--vibe-font-body, sans-serif);
          font-size: 1rem;
          font-weight: 300;
          line-height: 1.7;
          color: var(--vibe-color-text);
          margin: 0 auto;
          max-width: 68ch;
        }
        @media (min-width: 640px) {
          .hero-subtitle { font-size: 1.125rem; }
        }
        .hero-subtitle p {
          margin: 0;
          font-family: inherit;
          font-size: inherit;
          font-weight: inherit;
          line-height: 1.7;
        }
        .hero-subtitle p + p { margin-top: 0.75rem; }

        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.welcome': {
      label: 'Welcome Prefix',
      type: 'text',
      defaultValue: 'Welcome to',
    },
    'hero.name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Name',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultField: 'property_short_description',
      defaultValue: '<p>Discover thoughtfully designed apartment homes built for the way you want to live.</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
